/**
 * Popup View
 */

define(function(require, exports, module) {
    'use strict';
    var $ = require('j');
    var _ = require('underscore');
    var core = require('core');
    var BaseView = require('common/base.view');
    var device = require('util/device.util');
    var scrollManager = require('managers/scroll.manager');
    var dispatcher = require('bundle').dispatcher;

    /**
     * Low priority popups queue
     * @type {Array.<PopupView>}
     */
    var popups = [];

    /**
     * Queue index of popup currently shown on a page
     * @type {number}
     */
    var currentPopupIdx = 0;

    /**
     * High priority popups queue
     * @type {Array.<PopupView>}
     */
    var hiPriPopups = [];

    /**
     * Reference to the current page-as-popup shown on a page
     * @type {PopupView}
     */
    var currentPagePopup;

    /**
     * Reference to the current persistent popup
     * @type {PopupView}
     */
    var currentPersistentPopup;

    /**
     * Reference to the active popup
     * @type {PopupView}
     */
    var activePopup;

    /**
     * Represents current queue state. Frozen means queue is not processed while it is being modified in transaction
     * @type {boolean}
     */
    var frozen = false;

    var baseOverlayZIndex = 100;

    var filterPopups = function(popups, cid) { // eslint-disable-line no-shadow
        var predicate = function(item) {
            return item && item.cid === cid;
        };
        var index;

        while ((index = _.findIndex(popups, predicate)) > -1) {
            popups.splice(index, 1);
        }
    };

    /**
     * @class
     * @name PopupView
     * @augments BaseView
     */
    var PopupView = BaseView.extend(/** @lends PopupView.prototype */ {

        defaults: {
            /**
             * Callback for default close action
             * @return {Function}
             */
            onClose: function() {
            },

            /**
             * Template name for popup
             * @type {String}
             */
            tmpl: 'popup.modal-style',

            /**
             * Base data
             * @type {Object}
             */
            data: {
                showNextButton: false,
                showPrevButton: false,
                /**
                 * WebContent Id
                 */
                articleId: null,
                /**
                 * Whether to append WebContent to provided content or replace it
                 */
                append: false,
                /**
                 * Popup description
                 */
                description: '',
                /**
                 * Popup content (allows HTML)
                 */
                content: ''
            },

            /**
             * Close all popups if current is opened
             * @type {Boolean}
             */
            closeAll: false,

            /**
             * If popup has high priority it will be shown in separate queue,
             * else it will be shown in order with other ones
             * ('low' || 'high')
             * @type {String}
             */
            priority: 'low',

            /**
             * Defines order within regular low priority popups - they are sorted on popup add.
             * Popups with higher order come first.
             */
            order: 0,

            /**
             * Persistent popup behaves as page popup staying visible unaffected by other popups
             * @type {bool}
             */
            persistent: false,

            /**
             * If popup is modal it can't be closed by clicking on overlay
             * @type {Boolean}
             */
            isModal: false,

            /**
             * Do not process replacers on popup content
             * @type Boolean
             */
            ignoreReplacers: false
        },

        className: 'popup popup-modal',

        originalEvents: {
            'tap .fn-close': 'closeAsync',
            'tap .fn-next': 'navNext',
            'tap .fn-prev': 'navPrev',
            'focusin input': 'handleInputFocusBlur',
            'focusout input': 'handleInputFocusBlur'
        },

        additionalEvents: {
            // events for inherited views
        },

        /**
         * Shows if page shown as popup was already rendered. Used only for pages and persistent popups
         * @type {boolean}
         */
        pageRendered: false,

        /**
         * Applies options for inherited view. Should be overridden in descendants
         * @abstract
         * @param {Object} options - Initial options passed to constructor
         */
        setOptions: function(options) { // eslint-disable-line no-unused-vars
            // options for inherited views
        },

        events: function() {
            return _.extend({},
                _.result(this, 'originalEvents'),
                _.result(this, 'additionalEvents'),
                this.options && _.result(this.options, 'additionalEvents'));
        },

        initialize: function(options) {
            this.eventId = '.popup_' + this.cid;
            this.$el.attr('id', this.cid); // this added to allow testing
            this.options = _.extend({}, this.defaults, options);
            this.options.data = _.extend({}, this.defaults.data, this.options.data);
            this.setOptions(options);

            if (this.options.closeAll) {
                PopupView.closeAll(this);
            }

            this.addToQueue();

            this.articleId = this.options.data.articleId;

            this.processQueue();

            frozen = false;
        },

        render: function() {
            PopupView.hideAll(this);

            this._prepareRenderControls();
            this.renderPopup();

            // If we "hide" popup we will lose basic event handlers => restore them on render
            this.delegateEvents();

            this.attachClosingEvents();

            scrollManager.disableScrolling(this);

            if (this.options.pageModel) {
                this.renderPage();
            } else if (this.options.persistent) {
                this.pageRendered = true;
            }

            if (this.articleId) {
                this.loadArticle();
            }

            core.trigger('popup:rendered', this.options.data);

            // for excluding broken popup after rotation or scroll need to hide keyboard in special cases
            if (device.os === 'ios' && device.browserName === 'safari') {
                this.$el.on('scroll' + this.eventId, '.fn-popup-content', this.hideKeyboard.bind(this));
            } else if (device.os === 'android' && device.browserName === 'androidNative') {
                $(window).on('orientationchange' + this.eventId, this.hideKeyboard.bind(this));
            } else if (device.browserName === 'androidNative' && device.alias === 'Lenovo_A850') {
                $.event.add(window, 'orientationchange' + this.eventId, function() {
                    _.delay(this.redraw.bind(this, this.el, 750), 1000);
                }.bind(this));
            }

            // TODO: Remove current hotfix when ios 11 will fix
            // inputs with fixed position (https://bugs.webkit.org/show_bug.cgi?id=176896)
            this.HARD_FIX_FOR_IOS_11();

            return this;
        },

        /**
         * [HARD_FIX_FOR_IOS_11 REMOVE AFTER FIX BUG ON IOS] COR-13529
         */
        HARD_FIX_FOR_IOS_11: function() {
            var selector, body, scrollTop, content;

            function moveHandler(e) {
                var noSrollBar = content.scrollHeight === content.offsetHeight;

                if (noSrollBar || !content.contains(e.target)) {
                    e.preventDefault();
                }
            }

            function rotateHandler() {
                document.activeElement.blur();
            }

            if (device.formFactor !== 'desktop'
                && device.os === 'ios'
                && device.browserName === 'safari'
                && (device.osVersion.match('11.1') || device.osVersion.match('11.2'))) {

                selector = 'text,email,password,number,tel'.replace(/(\w+)/g, 'input[type="$1"]');
                body = document.body;
                scrollTop = body.scrollTop + 'px';
                this.prevBodyScrollTop = scrollTop; // save value, when popup open from popup

                // use timeout, because extended views change layout
                setTimeout(function hardFixForIos11() {
                    if (this.$(selector).length) {
                        if (popups.length) {
                            scrollTop = popups[0].prevBodyScrollTop;
                        }
                        this.el.style.top = scrollTop;
                        content = this.$('.fn-popup-content')[0];
                        body.classList.add('ios_11_fix');
                        body.addEventListener('touchmove', moveHandler);
                        window.addEventListener('orientationchange', rotateHandler);
                    }
                }.bind(this), 200);

                core.once('popup:closed', function() {
                    body.classList.remove('ios_11_fix');
                    body.removeEventListener('touchmove', moveHandler);
                    window.removeEventListener('orientationchange', rotateHandler);
                });

            }
        },

        renderOverlay: function() {
            BaseView.prototype.renderOverlay.apply(this, arguments);

            var zIndex = baseOverlayZIndex;

            // set overlay zIndex based on current layers
            if (hiPriPopups.length || popups.length) {
                zIndex += 4;
            } else if (currentPersistentPopup) {
                zIndex += 2;
            }

            this.$overlay.css('z-index', zIndex);
        },

        _prepareRenderControls: function() {
            var popupButtons = this.options.data;

            if (this.options.pageModel) {
                this.options.data.isPage = true;
            } else if (hiPriPopups.length) {
                popupButtons.showNextButton = false;
                popupButtons.showPrevButton = false;
                this.options.data.popupCount = 0;
            } else {
                popupButtons.showNextButton = currentPopupIdx < popups.length - 1;
                popupButtons.showPrevButton = currentPopupIdx > 0;
                this.options.data.popupCount = popups.length;
                this.options.data.currentPopup = currentPopupIdx + 1;
            }
        },

        renderPopup: function() {
            this.renderOverlay();

            this.options.data.isModal = this.options.isModal;

            const { $el, width, height } = this.options.data;

            let content = this.tmpl(this.options.tmpl, this.options.data);
            if (!this.options.ignoreReplacers) {
                content = content.replace();
            }
            this.$el.html(content);

            if ($el) {
                this.$('.fn-popup-loader').html($el);
            }

            if (this.articleId) {
                if (width) {
                    this.el.style.maxWidth = width;
                }

                if (height) {
                    this.$('.fn-popup-modal-inner').css('height', height);
                }
            }

            let containerCls;
            if (this.options.pageModel) {
                containerCls = '.fn-popup-page-container';
            } else if (this.options.persistent) {
                containerCls = '.fn-popup-persistent-container';
            } else if (this.options.priority === 'high') {
                containerCls = '.fn-popup-hi-container';
            } else {
                containerCls = '.fn-popup-container';
            }
            this.$parentEl = $(containerCls);
            this.$parentEl.addClass('showPopup');
            this.$el.appendTo(containerCls);
        },

        attachClosingEvents: function() {
            if (this.options.isModal) {
                return;
            }

            if (!this.options.pageModel && !this.options.persistent) {
                $(window).on('keydown' + this.eventId, this.closeByKeyDown.bind(this));
            }
        },

        closeByKeyDown: function(e) {
            if (e.keyCode === 27) {
                this.closeAsync(e);
            }
        },

        detachEvents: function() {
            this.$el.off(this.eventId);
            this.$parentEl && this.$parentEl.off(this.eventId);
            $(window).off(this.eventId);
        },

        /**
         * Renders page to show it as popup
         */
        renderPage: function() {
            var popupWidth = this.options.pageModel.get('popupWidth');
            var popupWidthUnit = this.options.pageModel.get('popupWidthUnit');

            this.el.style.width = this.el.style.maxWidth = (popupWidth > 0 ? popupWidth : 40) + (popupWidthUnit || '%');

            this.pageViewInstance = new this.options.pageView({
                model: this.options.pageModel,
                isPopup: true
            });
            this.$('.fn-popup-loader').html(this.pageViewInstance.render().$el);
            this.pageRendered = true;

            core.trigger('pageInPopup:rendered');
        },

        loadArticle: function() {
            return this.loadContent(this.articleId)
                .done(function(article) {
                    this.options.data.article = this.parseReplacers(article, this);
                    this.renderContent();
                }.bind(this));
        },

        renderContent: function() {
            let articleContainer;

            if (this.options.data.append) {
                articleContainer = this.$('.popup-loader');
                articleContainer.removeClass('popup-loader').removeClass('spinner');
                articleContainer.addClass('gamesinfo__description');
            } else {
                articleContainer = this.$('.fn-popup-loader');
            }

            articleContainer.html(this.options.data.article);
        },

        renderNav: function() {
            this.hide();

            return this.processQueue();
        },

        navNext: function() {
            currentPopupIdx++;
            return this.renderNav();
        },

        navPrev: function() {
            currentPopupIdx--;
            return this.renderNav();
        },

        handleInputFocusBlur: function(e) {
            if (device.formFactor !== 'desktop' && device.browserName === 'uc') {
                var isNeedTopPosition = e.type === 'focusin' ? !device.isKeyboardPresent : device.isKeyboardPresent;

                this.$el.toggleClass('popup-modal--position-top', isNeedTopPosition);
            }
        },

        /**
         * Adds this popup to queue and updates index
         * @returns {boolean}
         */
        addToQueue: function() {
            if (this.options.pageModel) {
                // replace page as popup
                currentPagePopup = this;
            } else if (this.options.persistent) {
                // replace persistent popup
                currentPersistentPopup && currentPersistentPopup.close();
                currentPersistentPopup = this;
            } else if (this.options.priority === 'high') {
                // replace high priority popup
                hiPriPopups.push(this);
            } else {
                // add regular popup to queue
                popups.push(this);
                popups.sort(function(a, b) {
                    return b.options.order - a.options.order;
                });
                if (popups[popups.length - 1] !== this) {
                    currentPopupIdx = 0;
                }
                // if hi pri popup is present do nothing
                if (hiPriPopups.length) {
                    // freeze queue to remove flicker on receiving unimportant message
                    frozen = true;
                }
            }
        },

        /**
         * Removes this popup from queue and modifies popup index
         */
        removeFromQueue: function() {
            var length;
            if (this.options && this.options.pageModel) {
                currentPagePopup = null;
                return;
            }

            if (this.options && this.options.persistent) {
                currentPersistentPopup = null;
                return;
            }

            // Filtering by cid ensures we don't get duplicate messages
            length = hiPriPopups.length;
            filterPopups(hiPriPopups, this.cid);

            if (hiPriPopups.length !== length) {
                return;
            }

            length = popups.length;
            filterPopups(popups, this.cid);

            if (!popups.length) {
                currentPopupIdx = 0;
            } else if (popups.length !== length && !popups[currentPopupIdx]) {
                currentPopupIdx--;
            }
        },

        /**
         * Represents Popup Manager main flow
         * @returns {*}
         */
        processQueue: function(isFrozen) {
            var popup;
            // process popups from top-most to bottom: order is important!

            if (isFrozen) {
                return this;
            }

            if (currentPagePopup) {
                if (currentPagePopup.pageRendered) {
                    currentPagePopup.renderOverlay();
                } else {
                    currentPagePopup.render();
                }

                activePopup = currentPagePopup;
            }

            if (hiPriPopups.length) {
                popup = hiPriPopups[hiPriPopups.length - 1];
                activePopup = popup;

                return popup.render();
            }

            if (popups.length) {
                popup = popups[currentPopupIdx];
                activePopup = popup;

                return popup.render();
            }

            if (currentPersistentPopup) {
                activePopup = currentPersistentPopup;

                if (currentPersistentPopup.pageRendered) {
                    return currentPersistentPopup.renderOverlay();
                }
                return currentPersistentPopup.render();
            }

            return this;
        },

        hide: function() {
            this.detachEvents();
            this.$el && this.$el.remove();
        },

        closeAsync: function() {
            return this.close(null, null, true);
        },

        /**
         * Closes the popup. Contains essential logic on popup closing flow.
         * @param [callback]
         * @param [closedByNewPageInPopup]
         * @param [async] {boolean} Makes method asynchronous. This is the only way to play closing animation.
         * @returns {Deferred}
         */
        close: function(callback, closedByNewPageInPopup, async) {
            var status = $.Deferred();

            this.removeFromQueue();

            this.detachEvents();
            this.hideKeyboard();

            var endClose = function() {
                if (this.pageViewInstance) {
                    BaseView.prototype.close.call(this.pageViewInstance);
                    delete this.pageViewInstance;
                }

                BaseView.prototype.close.call(this);

                this.removeOverlay();
                this.$el.removeClass('hide-popup');
                this.$parentEl && this.$parentEl.removeClass('showPopup');

                if (_.isFunction(this.options && this.options.onClose)) {
                    this.options.onClose(this);
                }
                if (_.isFunction(callback)) {
                    callback(this);
                }

                activePopup = null;

                scrollManager.enableScrolling(this);
                status.resolve();
                core.trigger('popup:closed', this.options);
                this.processQueue(frozen);
            }.bind(this);

            if (!frozen && async) {
                this.$el.addClass('hide-popup');
                this.transitionEnd(this.$el, endClose, this);
            } else {
                endClose();
            }

            status.done(function() {
                if (this.options && this.options.pageView && !closedByNewPageInPopup) {
                    dispatcher?.dispatch('pageInPopup:closed');
                }
            }.bind(this));

            return status;
        }

    });

    /**
     * Close all popups
     * @param {PopupView} [exclusionPopup] do not close this instance of popup
     * @param {Boolean} [isAllTypes] close popups of all types
     */
    PopupView.closeAll = function(exclusionPopup, isAllTypes) {
        frozen = true;

        if (isAllTypes) {
            currentPagePopup && currentPagePopup.close();
            currentPersistentPopup && currentPersistentPopup.close();
        }

        PopupView._processAll(exclusionPopup, 'close');
    };

    /**
     * Hide all low and high priority popups
     * but doesn't close other types
     * @param  {PopupView}   exclusionPopup
     */
    PopupView.hideAll = function(exclusionPopup) {
        PopupView._processAll(exclusionPopup, 'hide');
    };

    /**
     * Handle closing and hiding of popups
     * @param  {PopupView} exclusionPopup
     * @param  {String} action
     */
    PopupView._processAll = function(exclusionPopup, action) {
        // start override
        // Overridden By Licensee, need to keep loss limit popup and bonus popups after click on a link
        // https://portal-jira.playtech.corp/browse/SC-446
        frozen = true;
        var isArray = _.isArray(exclusionPopup);

        _.clone(popups).forEach(function(popup) {
            var isExclusionPopup = isArray
                ? _.contains(exclusionPopup, popup)
                : _.isEqual(exclusionPopup, popup);

            !isExclusionPopup && popup[action]();
        });

        _.clone(hiPriPopups).forEach(function(popup) {
            var isExclusionPopup = isArray
                ? _.contains(exclusionPopup, popup)
                : _.isEqual(exclusionPopup, popup);

            !isExclusionPopup && popup[action]();
        });

        frozen = false;
        // end override
    };

    PopupView.getCurrentPagePopup = function() {
        return currentPagePopup;
    };

    PopupView.getCurrentPersistentPopup = function() {
        return currentPersistentPopup;
    };

    PopupView.getActivePopup = function() {
        return activePopup;
    };

    /**
     * Returns true if any popup instance is present.
     * This method is used to handle overlay hiding and must be moved to OverlayManager once the latter is implemented.
     * @returns {boolean}
     */
    PopupView.getIsAnyPopupExist = function() {
        return Boolean(popups.length || hiPriPopups.length || currentPagePopup || currentPersistentPopup);
    };

    Object.defineProperties(PopupView, {
        'popups': {
            configurable: true,
            enumerable: true,
            get: function() {
                return popups;
            }
        },
        'hiPriPopups': {
            configurable: true,
            enumerable: true,
            get: function() {
                return hiPriPopups;
            }
        },
        'currentPagePopup': {
            configurable: true,
            enumerable: true,
            get: function() {
                return currentPagePopup;
            }
        },
        'currentPersistentPopup': {
            configurable: true,
            enumerable: true,
            get: function() {
                return currentPersistentPopup;
            }
        }
    });

    module.exports = PopupView;
});
